import { useContext, useEffect, useMemo, useState } from 'react';

import { ScrollView } from '@rbilabs/universal-components';

import {
  ModalPayloadType,
  OnShowModalType,
} from 'components/features/components/braze-marketing-card/types';
import linkSerializers from 'components/features/components/terms/terms-link';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import DrawerPage from 'components/layout/drawer-page';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import useDialogModal from 'hooks/use-dialog-modal';
import { BrazeContext } from 'state/braze';
import {
  filterContentCardsByServiceMode,
  getReadContentCardsFromLocalStorage,
  setReadContentCardsInLocalStorage,
} from 'state/braze/hooks/utils';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { useServiceModeLive } from 'state/service-mode';
import Braze, { ContentCard } from 'utils/braze';

import { CustomContentCard } from './content-cards';
import {
  CenteredLine,
  EmptyInboxCaption,
  EmptyInboxTitle,
  StyledPicture,
  UnreadSeparator,
  UnreadSeparatorText,
} from './styled';

type NotificationInboxProps = {
  handleClose: () => void;
};

export const NotificationInbox = ({ handleClose }: NotificationInboxProps) => {
  const dispatch = useAppDispatch();
  const contentCards = useAppSelector(selectors.loyalty.selectNewsFeedContentCards);
  const serviceMode = useServiceModeLive();
  const [unreadContentCardsIds, setUnreadContentCardsIds] = useState<string[]>([]);
  const [pendingTermsData, setPendingTermsData] = useState<ModalPayloadType>({
    content: [],
    heading: '',
  });
  const { logContentCardImpression } = useContext(BrazeContext);
  const [TermsModal, openTermsModal] = useDialogModal({
    modalAppearanceEventMessage: 'Terms and Conditions',
  });

  const onShowTermsModal: OnShowModalType = data => {
    openTermsModal(data);
    setPendingTermsData(data);
  };

  const visibleContentCards = useMemo(
    () => filterContentCardsByServiceMode(contentCards, serviceMode),
    [contentCards, serviceMode]
  );

  useEffect(() => {
    if (visibleContentCards.length === 0) {
      return;
    }

    const readCardsFromLocalStorage = getReadContentCardsFromLocalStorage();
    const unreadCardsIds = visibleContentCards
      .filter(({ id, viewed }) => !viewed && !readCardsFromLocalStorage.includes(id))
      .map(({ id }) => id);

    if (unreadCardsIds.length > 0) {
      setUnreadContentCardsIds(prevState => {
        return [...new Set([...prevState, ...unreadCardsIds])];
      });
    }

    let hasUnreadVisibleCards = false;
    visibleContentCards.forEach(({ id, viewed }) => {
      if (!viewed) {
        hasUnreadVisibleCards = true;
        setReadContentCardsInLocalStorage([id]);
        logContentCardImpression(id);
      }
    });
    if (hasUnreadVisibleCards) {
      dispatch(actions.loyalty.refreshUnreadContentCards(serviceMode));
    }
  }, [
    dispatch,
    logContentCardImpression,
    serviceMode,
    unreadContentCardsIds.length,
    visibleContentCards,
  ]);

  const orderedContentCards = useMemo(
    () =>
      visibleContentCards.reduce(
        (acc, cur) => {
          const key = unreadContentCardsIds.includes(cur.id) ? 'unread' : 'read';
          acc[key].push(cur);
          return acc;
        },
        { read: [] as ContentCard[], unread: [] as ContentCard[] }
      ),
    [unreadContentCardsIds, visibleContentCards]
  );

  useEffect(() => {
    return () => Braze.requestContentCardsRefresh();
  }, []);

  return (
    <DrawerPage
      headerText={t('news')}
      dismissType="close"
      onDismiss={handleClose}
      disableWebMaxWidth
    >
      <TermsModal
        heading={pendingTermsData.heading}
        bodyComponent={
          <ScrollView showsVerticalScrollIndicator={false}>
            <SanityBlockRenderer content={pendingTermsData.content} serializers={linkSerializers} />
          </ScrollView>
        }
      />
      {visibleContentCards.length === 0 ? (
        <>
          <StyledPicture source={require('./empty-state-image.png')} />
          <EmptyInboxTitle>{t('emptyInboxTitle')}</EmptyInboxTitle>
          <EmptyInboxCaption>{t('emptyInboxCaption')}</EmptyInboxCaption>
        </>
      ) : (
        <CenteredRowContainer>
          {orderedContentCards.unread.map((card, index) => (
            <CustomContentCard
              key={card.id}
              card={card}
              index={index}
              onShowTermsModal={onShowTermsModal}
            />
          ))}
          <UnreadSeparator>
            <CenteredLine />
            <UnreadSeparatorText>{t('contentCardsSeparatorLabel')}</UnreadSeparatorText>
            <CenteredLine />
          </UnreadSeparator>
          {orderedContentCards.read.map((card, index) => (
            <CustomContentCard
              key={card.id}
              card={card}
              index={orderedContentCards.unread.length + index}
              onShowTermsModal={onShowTermsModal}
            />
          ))}
        </CenteredRowContainer>
      )}
    </DrawerPage>
  );
};
